import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveQuizSocket } from "../../contexts/LiveQuizSocketContext";
import { useTranslation } from "../../hooks/useTranslation";
import { getMyCustomQuizzes, listModules } from "../../api/client";
import { BookOpen, Gamepad2 } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "https://app.fintogo.ai/api";

export default function LiveQuizTab({ classes, moduleId: moduleIdProp, lang = "de" }) {
  const { socket, setHostActiveRoom } = useLiveQuizSocket();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Modul-State (intern verwaltet falls kein moduleId-Prop)
  const [modules, setModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(moduleIdProp || null);

  // Modul-Quiz State
  const [quizzes, setQuizzes] = useState([]);
  const [selectedLessonId, setSelectedLessonId] = useState(null);

  // Custom-Quiz State
  const [customQuizzes, setCustomQuizzes] = useState([]);
  const [selectedCustomQuizId, setSelectedCustomQuizId] = useState(null);
  const [customQuizzesLoading, setCustomQuizzesLoading] = useState(false);
  const [customQuizzesError, setCustomQuizzesError] = useState(null);

  // Gemeinsamer State
  const [quizSource, setQuizSource] = useState("module"); // "module" | "custom"
  const [selectedClass, setSelectedClass] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Module laden falls nicht als Prop übergeben
  useEffect(() => {
    if (moduleIdProp) {
      setSelectedModuleId(moduleIdProp);
      return;
    }
    listModules()
      .then((data) => {
        const arr = Array.isArray(data) ? data : [];
        setModules(arr);
        if (arr.length > 0) setSelectedModuleId(arr[0].id);
      })
      .catch(console.error);
  }, [moduleIdProp]);

  // Modul-Quizzes laden
  useEffect(() => {
    if (!selectedModuleId) return;
    setQuizzes([]);
    setSelectedLessonId(null);
    const token = localStorage.getItem("token");
    fetch(
      `${API_BASE}/livequiz/modules/${selectedModuleId}/quizzes?lang=${lang}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    )
      .then(async (r) => {
        const text = await r.text();
        let data;
        try { data = JSON.parse(text); } catch { throw new Error(t.liveQuiz.loadError); }
        if (Array.isArray(data)) setQuizzes(data);
        else if (Array.isArray(data?.quizzes)) setQuizzes(data.quizzes);
        else setError(t.liveQuiz.invalidResponseFormat);
      })
      .catch(() => setError(t.liveQuiz.loadError));
  }, [selectedModuleId, lang]);

  // Eigene Quizzes laden wenn Tab gewechselt wird
  useEffect(() => {
    if (quizSource !== "custom") return;
    if (customQuizzes.length > 0) return;
    setCustomQuizzesLoading(true);
    setCustomQuizzesError(null);
    getMyCustomQuizzes()
      .then((data) => setCustomQuizzes(Array.isArray(data) ? data : []))
      .catch(() =>
        setCustomQuizzesError(
          t.liveQuiz.errorLoadingCustomQuizzes || "Fehler beim Laden der eigenen Quizzes."
        )
      )
      .finally(() => setCustomQuizzesLoading(false));
  }, [quizSource]);

  const handleCreate = async () => {
    const isModuleSource = quizSource === "module";
    if (isModuleSource && (!selectedLessonId || !selectedClass)) return;
    if (!isModuleSource && (!selectedCustomQuizId || !selectedClass)) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const endpoint = isModuleSource
        ? `${API_BASE}/livequiz/sessions`
        : `${API_BASE}/livequiz/sessions/custom`;

      const body = isModuleSource
        ? { moduleId: selectedModuleId, lessonId: selectedLessonId, classId: selectedClass, lang, snapshot: true }
        : { customQuizId: selectedCustomQuizId, classId: selectedClass, lang, snapshot: true, source: "custom" };

      const res = await fetch(endpoint, { method: "POST", headers, body: JSON.stringify(body) });

      const text = await res.text();
      let sessionRes;
      try {
        sessionRes = JSON.parse(text);
      } catch {
        throw new Error(`Server-Fehler (${res.status}): Ungültige Antwort vom Server.`);
      }

      if (!res.ok || sessionRes.error) throw new Error(sessionRes.error || `Fehler ${res.status}`);

      socket.emit("create_room", { roomCode: sessionRes.roomCode });
      socket.once("room_created", ({ roomCode }) => {
        setLoading(false);
        const quizTitle = isModuleSource
          ? quizzes.find((q) => q.key === selectedLessonId)?.title || "Live Quiz"
          : customQuizzes.find((q) => String(q.id) === String(selectedCustomQuizId))?.title || "Live Quiz";
        setHostActiveRoom({ roomCode, quizTitle });
        navigate(`/livequiz/lobby/${roomCode}`);
      });
      socket.once("create_room_error", ({ message }) => {
        setLoading(false);
        setError(message);
      });
    } catch (e) {
      setLoading(false);
      setError(e.message);
    }
  };

  const canCreate = selectedClass &&
    (quizSource === "module" ? !!selectedLessonId : !!selectedCustomQuizId);

  return (
    <div className="p-3 p-md-4 livequiz-tab">
      <h4 className="fw-bold mb-3">Live Quiz</h4>
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Tab-Umschalter: Modul vs. Eigene Quizzes */}
      <div className="mb-3">
        <div className="d-flex gap-2 flex-wrap">
          <button
            className={`btn btn-sm ${
              quizSource === "module" ? "btn-primary" : "btn-outline-secondary"
            } d-flex align-items-center gap-1`}
            onClick={() => setQuizSource("module")}
          >
            <BookOpen size={15} />
            {t.liveQuiz.moduleQuizzes || "Modul-Quizzes"}
          </button>
          <button
            className={`btn btn-sm ${
              quizSource === "custom" ? "btn-primary" : "btn-outline-secondary"
            } d-flex align-items-center gap-1`}
            onClick={() => setQuizSource("custom")}
          >
            <Gamepad2 size={15} />
            {t.liveQuiz.customQuizzes || "Eigene Quizzes"}
          </button>
        </div>
        <small className="text-muted d-block mt-1">
          {quizSource === "module"
            ? (t.liveQuiz.moduleQuizzesSubtitle || "Quizzes aus den Lernmodulen")
            : (t.liveQuiz.customQuizzesSubtitle || "Deine selbst erstellten Quizzes")}
        </small>
      </div>

      {/* Modul-Dropdown: nur wenn "Modul-Quizzes" aktiv und kein moduleId-Prop */}
      {quizSource === "module" && !moduleIdProp && modules.length > 1 && (
        <div className="mb-3">
          <label className="form-label fw-semibold mb-1">
            {t.liveQuiz.selectModule || "Modul auswählen"}
          </label>
          <select
            className="form-select"
            style={{ maxWidth: "100%", width: "fit-content", minWidth: "200px" }}
            value={selectedModuleId || ""}
            onChange={(e) => setSelectedModuleId(e.target.value)}
          >
            {modules.map((m) => (
              <option key={m.id} value={m.id}>
                {m.title || `Modul ${m.id}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Modul Quizzes: NUR wenn Modul-Quizzes ausgewählt */}
      {quizSource === "module" && (
        <div className="mb-4">
          {quizzes.length === 0 ? (
            <p className="text-muted">{t.liveQuiz.noQuizzes}</p>
          ) : (
            <div className="row g-2 g-md-3">
              {quizzes.map((q) => (
                <div className="col-12 col-sm-6 col-lg-4" key={q.key}>
                  <div
                    className={`card h-100 border-2 ${
                      selectedLessonId === q.key ? "border-primary bg-primary bg-opacity-10" : ""
                    }`}
                    onClick={() => setSelectedLessonId(q.key)}
                    style={{ cursor: "pointer", borderColor: selectedLessonId === q.key ? undefined : "var(--border-color)" }}
                  >
                    <div className="card-body py-2 px-3">
                      <h6 className="fw-bold mb-1">{q.title}</h6>
                      <small className="text-muted">
                        {(t.liveQuiz.questions || "{{count}} Fragen").replace("{{count}}", q.questionCount)}
                      </small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Eigene Quizzes */}
      {quizSource === "custom" && (
        <div className="mb-4">
          {customQuizzesLoading && (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
              <span className="text-muted small">{t.liveQuiz.loadingCustomQuizzes || "Lädt..."}</span>
            </div>
          )}
          {customQuizzesError && (
            <div className="alert alert-danger py-2 small">{customQuizzesError}</div>
          )}
          {!customQuizzesLoading && !customQuizzesError && customQuizzes.length === 0 && (
            <div className="text-center py-4">
              <Gamepad2 size={40} className="text-muted mb-2" />
              <p className="text-muted small mb-3">
                {t.liveQuiz.noCustomQuizzes || "Du hast noch keine eigenen Quizzes erstellt."}
              </p>
              <button className="btn btn-sm btn-outline-primary" onClick={() => navigate("/teacher/content")}>
                {t.liveQuiz.goToEditor || "Zum Quiz-Editor"}
              </button>
            </div>
          )}
          {!customQuizzesLoading && customQuizzes.length > 0 && (
            <div className="row g-2 g-md-3">
              {customQuizzes.map((q) => (
                <div className="col-12 col-sm-6 col-lg-4" key={q.id}>
                  <div
                    className={`card h-100 border-2 ${
                      String(selectedCustomQuizId) === String(q.id) ? "border-primary bg-primary bg-opacity-10" : ""
                    }`}
                    onClick={() => setSelectedCustomQuizId(String(q.id))}
                    style={{ cursor: "pointer", borderColor: String(selectedCustomQuizId) === String(q.id) ? undefined : "var(--border-color)" }}
                  >
                    <div className="card-body py-2 px-3">
                      <h6 className="fw-bold mb-1">{q.title || `Quiz #${q.id}`}</h6>
                      <small className="text-muted">
                        {(t.liveQuiz.questions || "{{count}} Fragen").replace("{{count}}", q.questionCount ?? q.question_count ?? "?")}
                      </small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Klasse auswählen + Starten */}
      <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-center">
        <select
          className="form-select"
          style={{ maxWidth: "100%" }}
          value={selectedClass}
          onChange={(e) => setSelectedClass(e.target.value)}
        >
          <option value="">{t.liveQuiz.selectClass}</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <button
          className="btn btn-primary px-4"
          onClick={handleCreate}
          disabled={!canCreate || loading}
        >
          {loading ? t.liveQuiz.creatingRoom : t.liveQuiz.createRoom}
        </button>
      </div>
    </div>
  );
}
